import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import { useMemo } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useTemplateContext } from '../../context/templateContext'
import type {
  TemplateReference,
  TemplateReferenceValueSetupField,
} from '../../types/templateModel'
import { generateEmptyTemplateValueSetup } from '../../uiModels/createEmptyFieldHelpers'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { indexedFieldErrors } from '../templateFieldsTable/fieldsHelpers'
import { TemplateReferencesExpressionsTable } from './TemplateReferencesExpressionsTable'
import { TemplateReferencesMappingTable } from './TemplateReferencesMappingTable'

type Props = {
  field: TemplateReferenceValueSetupField
  isExpanded?: boolean
  errors?: Optional<
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
}

export const TemplateReferenceValuesTable = ({
  field,
  isExpanded,
  errors,
}: Props) => {
  const translate = useTranslate()
  const { templateVersion, isMappingsTemplate } = useTemplateContext()

  const {
    preferences: {
      common: { isUsingModal },
    },
  } = useLayoutContext()
  const indexedErrors = useMemo(
    () =>
      indexedFieldErrors({
        fieldPath: field.path,
        errors: errors ?? [],
        indexedFieldName: 'variants',
      }),
    [errors, field.path],
  )
  assertNotEmpty(templateVersion)

  const rowClassName =
    isUsingModal || !isExpanded
      ? paperStyles({ isFaded: false })
      : paperStyles({ isFaded: true })

  const value = field.value

  const handleVariantChanged = (
    nextVariantValue: Optional<TemplateReference>,
    position: number,
  ) => {
    const fieldValue = field.value ?? generateEmptyTemplateValueSetup(field)
    const nextVariants = [...fieldValue.variants]

    if (nextVariantValue) {
      const { origin, ...variantValueFields } = nextVariantValue
      nextVariants.splice(position, 1, {
        ...variantValueFields,
        origin: variantValueFields.path,
      })
    } else {
      nextVariants.splice(position, 1)
    }

    return field.update({
      ...fieldValue,
      variants: nextVariants,
      origin: fieldValue.path,
    })
  }

  const TableComponent = isMappingsTemplate
    ? TemplateReferencesMappingTable
    : TemplateReferencesExpressionsTable

  return (
    <>
      <TableComponent
        errors={indexedErrors}
        field={field}
        rowClassName={rowClassName}
        onChange={isExpanded ? handleVariantChanged : undefined}
        isExpanded={isExpanded}
      />
      {!value?.variants.length && (
        <Alert severity="warning" className="mt-2">
          {translate('templateViewer.templateReferenceViewer.modal.empty')}
        </Alert>
      )}
    </>
  )
}
